import type { ReactNode } from 'react'

import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { cn } from '@/lib/utils'

export interface ListTab<K extends string = string> {
  key: K
  label: ReactNode
  /** Rendered before the label — a lucide icon. */
  icon?: ReactNode
  /** Row count, shown greyed after the label. Hidden when undefined. */
  count?: number
}

export interface ListTabsProps<K extends string = string> {
  tabs: ListTab<K>[]
  value: K
  onValueChange: (key: K) => void
  /**
   * `segmented` (default) is the pill strip used in card headers; `line` is a
   * full-width strip of folder tabs on the top edge of a card, the active one a
   * white tile, for switching between whole sections of a page.
   */
  variant?: 'segmented' | 'line'
  className?: string
}

/**
 * A segmented tab strip for switching what a card shows, on the shadcn/Radix
 * {@link Tabs} primitive so keyboard and ARIA behaviour come from Radix.
 *
 * Unlike {@link RouteTabs} it owns nothing but the buttons: the panels are the
 * caller's own markup, and the active tab lives in the caller's state. Use it
 * wherever a card header switches between lists, or for a plain two-option
 * toggle.
 */
export function ListTabs<K extends string = string>({
  tabs,
  value,
  onValueChange,
  variant = 'segmented',
  className,
}: ListTabsProps<K>) {
  const line = variant === 'line'
  return (
    <Tabs
      value={value}
      onValueChange={(v) => onValueChange(v as K)}
      className={cn(line ? 'relative mt-3 w-full gap-0' : 'w-fit shrink-0 gap-0', className)}
    >
      {line && (
        // The top edge of a card that wraps the page content: its radius is the
        // cards' plus the outset so the corners run parallel, sides fading out.
        <div
          aria-hidden
          className="pointer-events-none absolute -inset-x-3 top-full h-12 rounded-t-[calc(var(--radius-xl)+--spacing(3))] border-x border-t border-(--ad-border-strong) mask-b-from-50%"
        />
      )}
      <TabsList
        variant={line ? 'line' : 'default'}
        className={cn(
          line &&
            'w-full justify-start gap-1 px-2 py-0 group-data-[orientation=horizontal]/tabs:h-12'
        )}
      >
        {tabs.map((tab) => (
          <TabsTrigger
            key={tab.key}
            value={tab.key}
            className={cn(
              line &&
                // A folder tab: the active one is a white tile with a thin border on
                // three sides, and its ::after covers the card edge beneath it so the
                // tile opens into the card. Variant chains match the primitive's so
                // tailwind-merge replaces its transparent line-variant overrides.
                'h-full flex-none gap-2 rounded-none rounded-t-lg px-4 font-semibold text-(--ad-text) dark:text-(--ad-text) [&_svg]:text-(--ad-text-muted) hover:bg-(--ad-surface-sunken) data-[state=active]:border-(--ad-border-strong) data-[state=active]:border-b-transparent data-[state=active]:text-primary dark:data-[state=active]:text-primary data-[state=active]:[&_svg]:text-primary group-data-[variant=line]/tabs-list:data-[state=active]:bg-(--ad-surface) dark:group-data-[variant=line]/tabs-list:data-[state=active]:bg-(--ad-surface) dark:group-data-[variant=line]/tabs-list:data-[state=active]:border-(--ad-border-strong) after:bg-(--ad-surface) group-data-[orientation=horizontal]/tabs:after:-bottom-px group-data-[orientation=horizontal]/tabs:after:h-px'
            )}
          >
            {tab.icon}
            {tab.label}
            {tab.count !== undefined && (
              <span className="text-xs text-(--ad-text-muted)">({tab.count})</span>
            )}
          </TabsTrigger>
        ))}
      </TabsList>
    </Tabs>
  )
}
